const KC_URL = process.env.REACT_APP_KC_URL || 'https://auth.staging.ronspot.ie';
const KC_REALM = process.env.REACT_APP_KC_REALM || 'ronspot-staging';
const KC_CLIENT_ID = process.env.REACT_APP_KC_CLIENT_ID || 'ronspot-react-app';
const KC_REDIRECT_URI = process.env.REACT_APP_KC_REDIRECT_URI || `${window.location.origin}/keycloak-callback`;
const LARAVEL_API = (process.env.REACT_APP_LARAVEL_API_URL || 'https://lvstaging.ronspot.ie/api/').replace(/\/$/, '');

const KC_AUTH_URL = `${KC_URL}/realms/${KC_REALM}/protocol/openid-connect/auth`;
const KC_TOKEN_URL = `${KC_URL}/realms/${KC_REALM}/protocol/openid-connect/token`;

function generateCodeVerifier() {
  const array = new Uint8Array(32);
  window.crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

async function generateCodeChallenge(verifier) {
  const data = new TextEncoder().encode(verifier);
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * Kick off the KC PKCE authorization code flow.
 * - idpHint ('microsoft' | 'google') skips the KC login page entirely and
 *   redirects the user straight to the identity provider.
 * - loginHint (user's email) is forwarded to the IDP so the email field is
 *   pre-filled — user only needs to enter their password.
 */
export async function startKCLogin(idpHint, loginHint, companyId) {
  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);
  const state = generateCodeVerifier();

  sessionStorage.setItem('kc_code_verifier', verifier);
  sessionStorage.setItem('kc_state', state);
  // Carry the company the user picked at discovery (for multi-company domains) so the
  // exchange can provision a new user into the right company.
  if (companyId) {
    sessionStorage.setItem('kc_company_id', String(companyId));
  } else {
    sessionStorage.removeItem('kc_company_id');
  }

  const params = new URLSearchParams({
    client_id: KC_CLIENT_ID,
    response_type: 'code',
    redirect_uri: KC_REDIRECT_URI,
    scope: 'openid email profile',
    code_challenge: challenge,
    code_challenge_method: 'S256',
    state,
    ...(idpHint ? { kc_idp_hint: idpHint } : {}),
    ...(loginHint ? { login_hint: loginHint } : {}),
  });

  window.location.href = `${KC_AUTH_URL}?${params.toString()}`;
}

/**
 * Exchange the authorization code returned by KC for KC tokens.
 * Returns { access_token, refresh_token, id_token, ... }.
 */
export async function exchangeKCCode(code, state) {
  const storedState = sessionStorage.getItem('kc_state');
  if (storedState && state && storedState !== state) {
    throw new Error('OAuth state mismatch — possible CSRF attack, aborting.');
  }

  const verifier = sessionStorage.getItem('kc_code_verifier');
  if (!verifier) {
    throw new Error('Missing PKCE code verifier — cannot complete sign-in.');
  }

  sessionStorage.removeItem('kc_code_verifier');
  sessionStorage.removeItem('kc_state');

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: KC_CLIENT_ID,
    redirect_uri: KC_REDIRECT_URI,
    code,
    code_verifier: verifier,
  });

  let res;
  try {
    res = await fetch(KC_TOKEN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
  } catch (e) {
    const err = new Error(`Network error reaching Keycloak token endpoint: ${e.message}`);
    err.details = { url: KC_TOKEN_URL, stage: 'fetch', raw: String(e.message) };
    throw err;
  }

  const rawText = await res.text();
  if (!res.ok) {
    const err = new Error(`KC token exchange failed (HTTP ${res.status})`);
    err.details = { url: KC_TOKEN_URL, httpStatus: res.status, raw: rawText.slice(0, 1500) };
    throw err;
  }

  try {
    return JSON.parse(rawText);
  } catch {
    const err = new Error(`KC token endpoint returned non-JSON (HTTP ${res.status})`);
    err.details = { url: KC_TOKEN_URL, httpStatus: res.status, raw: rawText.slice(0, 1500) };
    throw err;
  }
}

/**
 * Exchange a KC access token for a Ronspot JWT.
 * Returns the Records object: { guid, id, FirstName, LastName, PrimaryEmail, AccessToken, RefreshToken }.
 */
export async function exchangeKCTokenForRonspot(kcIdToken, extra = {}) {
  const url = `${LARAVEL_API}/v6/keycloak-exchange`;

  let res;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ keycloak_token: kcIdToken, ...extra }),
    });
  } catch (e) {
    const err = new Error(`Network error reaching Ronspot exchange: ${e.message}`);
    err.details = { url, stage: 'fetch', raw: String(e.message) };
    throw err;
  }

  const rawText = await res.text();
  let data;
  try {
    data = JSON.parse(rawText);
  } catch {
    const err = new Error(`Ronspot exchange returned non-JSON (HTTP ${res.status})`);
    err.details = { url, httpStatus: res.status, raw: rawText.slice(0, 1500) };
    throw err;
  }

  const responseCode = data?.Data?.ResponseCode;
  if (responseCode !== 200) {
    const err = new Error(data?.Data?.ErrorMessage || 'Ronspot account exchange failed.');
    err.details = {
      url,
      httpStatus: res.status,
      responseCode,
      message: data?.Data?.Message,
      errorMessage: data?.Data?.ErrorMessage,
    };
    throw err;
  }

  return data.Data.Records;
}

/** True when the KC integration is configured. */
export const isKCEnabled = Boolean(
  process.env.REACT_APP_KC_URL && process.env.REACT_APP_KC_CLIENT_ID
);
